import { useEffect, useRef, useState } from "react"
import { Controller, Resolver, useForm } from "react-hook-form"
import { FiEdit } from "react-icons/fi"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"

import { useEmailVerification } from "@fuse/hooks/useEmailVerification"
import BackgroundWrapper from "@fuse/sharedComponents/backgroundWrapper"
import BlurWrapper from "@fuse/sharedComponents/blurWrapper"
import { blurKeys } from "@fuse/sharedComponents/blurWrapper/blurKeysList"
import { EventPluginsRenderer } from "@fuse/sharedComponents/EventPluginsLoader"
import { useAlert } from "@fuse/sharedComponents/globalAlert"
import { useSignUpModalState } from "@fuse/sharedComponents/globalQuickSignUpModal"
import LoadingSpinner from "@fuse/sharedComponents/loader/LoadingSpinner"
import NextStepsModal from "@fuse/sharedComponents/nextStepsModal"
import {
  showErrorToast,
  showInfoToast,
  showSuccessToast,
} from "@fuse/sharedComponents/themeToast/toastUtils"
import { yupResolver } from "@hookform/resolvers/yup"
import {
  EDefaultPayloadData,
  EEVENT_INVITATION_CRITERIA,
  EEventType,
  EFeatureFlags,
  EGENDERS,
  EVENT_CLASS,
  EVENT_STATUS,
  EVENT_TICKET_TYPE,
  PAYMENT_TYPE,
} from "@letshang/constants"
import { getIdToken, signInWithCustomToken, signOut } from "firebase/auth"
import { get, isEmpty } from "lodash-es"
import moment from "moment"
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil"
import { ROUTES } from "src/App"
import * as yup from "yup"

import { userState } from "@fuse/recoil/signup"
import { defaultUser } from "@fuse/recoil/signup"
import { stripeUrlState } from "@fuse/recoil/stripe"

import { auth } from "@firebaseConfig/clientApp"
import { getRemoteConfigBoolean } from "@firebaseConfig/remoteConfigUtil"

import { EFailureMessage } from "@settingsAlias/constants"
import { useProcessFeatureFlags } from "@settingsAlias/featureFlags"

import { createEventWeb, onboardUserToStripe } from "@api/firebaseWrappedApis"
import { resendEmailVerificationAPI } from "@api/firebaseWrappedApis"
import {
  useAddTicket,
  useGetAllTickets,
} from "@api/services/events/useEventsApi"

import { convertImageToBase64, ensureLetsHangHttps } from "@fuse/utils/common"
import { isDev } from "@fuse/utils/DeviceDection"

import useClarityMonitor from "@fuse/customHooks/useClarityMonitor"
import { useTimezone } from "@fuse/customHooks/useTimezone"

import { getTheLocationInfo } from "@fuse/pages/explore/utility/locationFunctions"

// Import the default background image
import eventDefaultBg from "@fuse/assets/images/event-default-bg.png"

import DateTimePickerModal, {
  DateTimePickerModalHandle,
} from "../components/dateTimePicker/DateTimePicker"
import EmailVerificationPopup from "../components/EmailVerificationPopup"
import EventPluginBottomSheet, {
  EventPluginBottomSheetHandle,
} from "../components/eventPlugins/eventPluginsBottomSheet"
import EventAddAPluginSection from "../components/eventPlugins/eventPluginsSection"
import LocationBottomSheet, {
  LocationBottomSheetHandle,
} from "../components/locationPicker/LocationBottomSheet"
import DescriptionBottomSheet, {
  DescriptionBottomSheetHandle,
} from "../createEvent/DescriptionBottomSheet"
import MoreComponent from "../createEvent/MoreComponent"
import { VideoPreviewExperiment } from "../createEvent/VideoPreviewExperiment"
import FastSignUpComponentWithGTM from "../FastSignUpComponentWithGTM"
import useFetchEventDetails from "../useFetchEventDetails"
import TicketModal from "./TicketModal-v5"

const bgImage = eventDefaultBg

const now = moment()
// move to the next full hour (even if that's later today)
const nextHour = now.clone().add(2, "hour").startOf("hour")

const initialStartDate = nextHour.format("MMMM DD YYYY")
const initialStartTime = nextHour.format("hh:00 A")
const endMoment = nextHour.clone().add(1, "hour")
const initialEndDate = endMoment.format("MMMM DD YYYY")
const initialEndTime = endMoment.format("hh:00 A")

type EventFormData = {
  eventName: string
  startDate: string
  endDate: string
  startTime: string
  endTime: string
  location: {
    lat: number
    lon: number
    nameOfPlace: string
  }
  description: string
  flyerImage: FileList | null | string
}

const schema = yup
  .object({
    eventName: yup.string().required("Event name is required"),
    startDate: yup.string().required("Start date is required"),
    endDate: yup.string().required("End date is required"),
    startTime: yup.string().required("Start time is required"),
    endTime: yup.string().required("End time is required"),
    location: yup
      .object({
        lat: yup.number().required(),
        lon: yup.number().required(),
        nameOfPlace: yup.string().required(),
      })
      .required("Location is required"),
    description: yup.string().required("Description is required"),
    flyerImage: yup.mixed<FileList | null | string>().nullable(),
  })
  .required()

const CreateEventPage = () => {
  const [user, setUser] = useRecoilState(userState)

  // Add Clarity monitoring
  useClarityMonitor()

  const [reDirectedFrom, setReDirectedFrom] = useState("")
  const [flyerFile, setFlyerFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string>(bgImage)
  const [showMore, setShowMore] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [createdEventId, setCreatedEventId] = useState("")
  const [isCreatingEmptyEvent, setIsCreatingEmptyEvent] = useState(false)
  const [showTicketModal, setShowTicketModal] = useState(false)
  const isIntegrationsEnabled = useProcessFeatureFlags(
    EFeatureFlags.ENABLE_ADD_PLUGIN_TO_EVENT,
  )
  const isFlexibleEventCreationEnabled = useProcessFeatureFlags(
    "flexible_event_creation" as any,
  )
  const { state, search, key: locationKey } = useLocation()
  const { state: eventState, refresh } = useFetchEventDetails(createdEventId)
  const [isFromCompleteProfile, setIsFromCompleteProfile] = useState(() => {
    const isFromCompleteProfile = state?.isFromCompleteProfile
    if (isFromCompleteProfile) {
      return true
    }
    return false
  })

  const [isUploaded, setIsUploaded] = useState({
    flyerImage: false,
    timingAndLocation: false,
  })
  // const ticketedEventsWebFeatureFlag =
  //   useProcessFeatureFlags(EFeatureFlags.TICKETED_EVENTS_WEB) || true
  const dateTimePickerModalRef = useRef<DateTimePickerModalHandle>(null)
  const onOpenDateTimePickerModal = () => {
    setIsDatePickerModalOpen(true)
    dateTimePickerModalRef.current?.openModal()
  }

  const descriptionBottomSheetRef = useRef<DescriptionBottomSheetHandle>(null)
  const onOpenDescriptionBottomSheet = () => {
    descriptionBottomSheetRef.current?.openModal()
  }

  const { timezone } = useTimezone()
  const navigate = useNavigate()

  const [showEmailVerificationPopup, setShowEmailVerificationPopup] =
    useState(false)
  const [isEmailInboxVerification, setIsEmailInboxVerification] =
    useState(false)

  // Use consolidated email verification hook
  const {
    verifyEmail,
    isLoading: loadingEmailVerification,
    startPolling,
  } = useEmailVerification()

  // Email verification functions - now simplified using the hook
  const onVerifyEmail = async (email: string) => {
    const result = await verifyEmail(email, {
      onSuccess: () => {
        setIsEmailInboxVerification(true)
        // Success callback for both dev and production flows
      },
      onCloseUI: () => {
        // Dev mode: immediate verification, close UI and continue
        setShowEmailVerificationPopup(false)
        setIsEmailInboxVerification(false)
      },
      onRequireInboxCheck: () => {
        // Production mode: show inbox check UI
        setIsEmailInboxVerification(true)

        // Start polling for verification with cleanup
        startPolling(() => {
          setShowEmailVerificationPopup(false)
          setIsEmailInboxVerification(false)
          createEventDirectly()
        })
      },
      onVerificationComplete: () => {
        // Handle completion callback if needed
      },
    })

    return result
  }

  const handleEmailVerificationClose = () => {
    setShowEmailVerificationPopup(false)
    setIsEmailInboxVerification(false)
  }

  const [showNextSteps, setShowNextSteps] = useState(() => {
    const isFromCompleteProfile = localStorage.getItem("isFromCompleteProfile")
    if (isFromCompleteProfile === "true") {
      localStorage.removeItem("isFromCompleteProfile")
      return true
    }
    return false
  })
  const [isDatePickerModalOpen, setIsDatePickerModalOpen] = useState(false)
  const [templateState, setTemplateState] = useState(() => {
    const saved = JSON.parse(localStorage.getItem("savedEventDetails") || "{}")
    if (!isEmpty(saved)) localStorage.removeItem("savedEventDetails")
    const baseTemplate = !isEmpty(state?.template) ? state?.template : saved
    return {
      ...baseTemplate,
      tickets: saved?.tickets || [], // Always include tickets from localStorage
    }
  })

  const queryParams = new URLSearchParams(search)
  const referenceFrom = queryParams.get("reference")
  const { data: ticketsData, refetch: refetchTickets } =
    useGetAllTickets(createdEventId)
  const apiTickets = get(ticketsData, "data.tickets", [])
  const { mutate: addTicketMutation } = useAddTicket()

  // State for tickets - combines localStorage and API data
  const [tickets, setTickets] = useState(() => {
    // If we have an eventId, prefer API data, otherwise use localStorage
    return createdEventId ? apiTickets : templateState?.tickets || []
  })

  // Calculate ticket price range from either API data or local tickets
  const apiMinTicketPrice = get(eventState.eventDetails, "minTicketPrice", 0)
  const apiMaxTicketPrice = get(eventState.eventDetails, "maxTicketPrice", 0)

  // Calculate min/max from local tickets if no API data
  const localMinTicketPrice =
    tickets.length > 0 ? Math.min(...tickets.map((t) => t.ticketPrice || 0)) : 0
  const localMaxTicketPrice =
    tickets.length > 0 ? Math.max(...tickets.map((t) => t.ticketPrice || 0)) : 0

  // Use API data if available, otherwise use local data
  const minTicketPrice =
    apiMinTicketPrice > 0 ? apiMinTicketPrice : localMinTicketPrice
  const maxTicketPrice =
    apiMaxTicketPrice > 0 ? apiMaxTicketPrice : localMaxTicketPrice
  const hasTickets = tickets.length > 0
  const isSingleTicket = minTicketPrice === maxTicketPrice

  // Track if we've attempted to sync local tickets to prevent multiple attempts
  const syncAttempted = useRef(false)

  // Simple one-time sync function
  // NOTE: This is a basic implementation that doesn't handle complex edge cases like:
  // - Partial sync failures
  // - Ticket conflicts during sync
  // - Retry mechanisms
  // - User feedback for sync status
  const syncLocalTicketsToAPI = async () => {
    if (syncAttempted.current || !createdEventId || tickets.length === 0) {
      return
    }

    syncAttempted.current = true
    console.log(
      `Syncing ${tickets.length} local tickets to API for event ${createdEventId}...`,
    )

    try {
      let syncCount = 0

      // Sync tickets one by one to avoid race conditions
      for (const ticket of tickets) {
        try {
          await new Promise((resolve, reject) => {
            // Remove the temp id field before syncing
            const { id, ...ticketDataWithoutId } = ticket

            addTicketMutation(
              {
                eventId: createdEventId,
                ticketData: {
                  ticketType: EVENT_TICKET_TYPE.PAID_TICKET,
                  paymentType: PAYMENT_TYPE.MANUAL,
                  ...ticketDataWithoutId,
                  isActive: true,
                },
              },
              {
                onSuccess: (response) => {
                  if (response?.success) {
                    syncCount++
                    resolve(response)
                  } else {
                    reject(
                      new Error(response?.message || "Failed to sync ticket"),
                    )
                  }
                },
                onError: (error) => {
                  reject(error)
                },
              },
            )
          })
        } catch (ticketError) {
          console.error("Failed to sync individual ticket:", ticketError)
        }
      }

      console.log(
        `Local tickets sync completed: ${syncCount}/${tickets.length} tickets synced, refreshing API data...`,
      )

      // Refresh API tickets to get the synced data
      refetchTickets()
    } catch (error) {
      console.error("Error syncing local tickets to API:", error)
      // Reset flag to allow retry on next transition
      syncAttempted.current = false
    }
  }

  // Fixed sync logic to prevent race condition
  useEffect(() => {
    if (createdEventId && apiTickets.length > 0) {
      // API has tickets - use them (normal case)
      setTickets(apiTickets)
      syncAttempted.current = true // Mark as synced since API has data
    } else if (
      createdEventId &&
      apiTickets.length === 0 &&
      tickets.length > 0 &&
      !syncAttempted.current
    ) {
      // We have local tickets but API is empty - sync them to API
      syncLocalTicketsToAPI()
    }
  }, [createdEventId, apiTickets])

  // Auto-save tickets to localStorage when they change (for unauthenticated users)
  useEffect(() => {
    if (!createdEventId && tickets.length >= 0) {
      // Only save if tickets have actually changed from initial state
      const currentSaved = JSON.parse(
        localStorage.getItem("savedEventDetails") || "{}",
      )
      if (
        JSON.stringify(currentSaved.tickets || []) !== JSON.stringify(tickets)
      ) {
        saveToLocalStore()
      }
    }
  }, [tickets, createdEventId])

  useEffect(() => {
    if (createdEventId) {
      refresh()
    }
  }, [createdEventId, showMore, tickets.length])

  useEffect(() => {
    if (templateState?.eventId && !templateState?.isTemplate) {
      setCreatedEventId(templateState.eventId)
    }
  }, [templateState])

  // Removed forced redirect to signup - allow unauthenticated users to create events
  // Fast signup will be shown when they try to perform actions that require authentication

  useEffect(() => {
    if (flyerFile) {
      const objectUrl = URL.createObjectURL(flyerFile)
      setPreviewUrl(objectUrl)

      // Cleanup the object URL when component unmounts or file changes
      return () => URL.revokeObjectURL(objectUrl)
    } else if (templateState?.flyerImage) {
      setPreviewUrl(templateState.flyerImage)
    } else {
      // Use the default image if no file or templateState flyerImage
      setPreviewUrl(bgImage)
    }
  }, [flyerFile, templateState])

  const flyerImageUrl = previewUrl

  const saveToLocalStore = async () => {
    let toBeSavedEventDetails = {}

    if (createdEventId && eventState.eventDetails) {
      toBeSavedEventDetails = {
        ...eventState.eventDetails.eventDocData,
        eventId: createdEventId,
        eventName: formState.eventName,
        eventDescription: formState.description,
        tickets: tickets, // Include current tickets
      }
    } else {
      toBeSavedEventDetails = {
        ...formState,
        flyerImage: "",
        tickets: tickets, // Include current tickets
      }
    }

    await localStorage.setItem(
      "savedEventDetails",
      JSON.stringify(toBeSavedEventDetails),
    )
  }

  function convertToUTC(dateTimeStr: string) {
    return moment(dateTimeStr, "MMMM DD YYYY hh:mm A").utc().toISOString()
  }

  const checkImageType = async (file: FileList | string) => {
    if (typeof file === "string") {
      // Handle URLs
      if (file.startsWith("http")) {
        return file
      }
      // Handle imported assets
      else if (file === bgImage) {
        const response = await fetch(bgImage)
        const blob = await response.blob()
        const base64String = await convertImageToBase64(blob)
        if (typeof base64String === "string") {
          return base64String.split(",")[1]
        }
        // For imported assets, we'll return null to indicate no upload is needed
        // The frontend will use the imported asset directly
        // return "https://firebasestorage.googleapis.com/v0/b/walkme-e7896.appspot.com/o/event%20assets%2F1.png?alt=media&token=a45a8b60-1c42-4cb6-bfef-b78aba693a5c"
      }
      // Handle base64 strings
      else {
        return file.split(",")[1]
      }
    } else {
      // Handle FileList objects
      const base64String = await convertImageToBase64(file)
      if (typeof base64String === "string") {
        return base64String.split(",")[1]
      }
      return ""
    }
  }

  const {
    register,
    handleSubmit,
    formState: { errors, dirtyFields },
    control,
    watch,
    setValue,
    trigger,
  } = useForm<EventFormData>({
    resolver: yupResolver(schema) as unknown as Resolver<EventFormData>,
    defaultValues: {
      eventName: templateState?.title || templateState?.eventName || "",
      startDate: templateState?.startDate
        ? templateState?.startDate
        : templateState?.timing?.eventDate
          ? moment(templateState?.timing?.eventDate).format("MMMM DD YYYY")
          : initialStartDate,
      endDate: templateState?.endDate
        ? templateState?.endDate
        : templateState?.timing?.eventEndDate
          ? moment(templateState?.timing?.eventEndDate).format("MMMM DD YYYY")
          : initialEndDate,
      startTime: templateState?.startTime
        ? templateState?.startTime
        : templateState?.timing?.eventStartTime
          ? moment(templateState?.timing?.eventStartTime).format("hh:00 A")
          : initialStartTime,
      endTime: templateState?.endTime
        ? templateState?.endTime
        : templateState?.timing?.eventEndTime
          ? moment(templateState?.timing?.eventEndTime).format("hh:00 A")
          : initialEndTime,
      location: {
        lat: templateState?.location?.latitude || 0,
        lon: templateState?.location?.longitude || 0,
        nameOfPlace: templateState?.location?.eventLocation || "",
      },
      description:
        templateState?.description || templateState?.eventDescription || "",
      flyerImage: templateState?.flyerImage || flyerImageUrl,
    },
  })

  const formState = watch()
  const endDate = watch("endDate")
  const endTime = watch("endTime")
  const startDate = watch("startDate")
  const startTime = watch("startTime")
  const description = watch("description")
  const location = watch("location")
  const isGoliveClicked = useRef(false)

  const { fastSignUpStage, setShowFastSignUpPopUp, showFastSignUpPopUp } =
    useSignUpModalState()

  const defaultPayloadData = {
    minAge: EDefaultPayloadData.MIN_AGE,
    maxAge: EDefaultPayloadData.MAX_AGE,
    howManyPeople: [
      EDefaultPayloadData.MIN_PEOPLE,
      EDefaultPayloadData.MAX_PEOPLE,
    ],
    numberAttending: EDefaultPayloadData.MIN_PEOPLE,
    numberRemainingSpots: EDefaultPayloadData.MAX_PEOPLE,
    restrictMemberGender: EGENDERS.OPEN,
  }

  const createEventV2ForFlyerImage = async (eventId?: string) => {
    const finalFlyerImage = await checkImageType(formState?.flyerImage)
    const payloadData: any = {
      flyerImage: finalFlyerImage,
    }

    if (createdEventId || eventId) {
      payloadData.eventId = createdEventId || eventId
    }

    if (!isUploaded?.timingAndLocation) {
      payloadData.timing = {
        eventDate: convertToUTC(
          `${formState?.startDate} ${formState?.startTime}`,
        ),
        eventEndDate: convertToUTC(
          `${formState?.endDate} ${formState?.endTime}`,
        ),
        timeZone: "UTC",
        eventStartTime: convertToUTC(
          `${formState?.startDate} ${formState?.startTime}`,
        ),
        eventEndTime: convertToUTC(
          `${formState?.endDate} ${formState?.endTime}`,
        ),
        signupCloseDate: convertToUTC(
          `${formState?.startDate} ${formState?.startTime}`,
        ),
        signupCloseTime: convertToUTC(
          `${formState?.startDate} ${formState?.startTime}`,
        ),
        displayTimezone: timezone,
      }
    }
    payloadData.eventClass =
      tickets.length > 0
        ? EVENT_CLASS.TICKETED_EVENT
        : EVENT_CLASS.NON_TICKETED_EVENT

    await createEventWeb({ ...payloadData }).then((res) => {
      if (res.success) {
        setIsUploaded({ ...isUploaded, flyerImage: true })
        if (!createdEventId) {
          setCreatedEventId(res.data.eventId)
        }
      } else if (!res.success) {
        setIsUploaded({ ...isUploaded, flyerImage: false })
      }
    })
  }

  useEffect(() => {
    if (
      previewUrl !== bgImage &&
      !isCreatingEmptyEvent &&
      (!reDirectedFrom || (reDirectedFrom && reDirectedFrom !== "stripe"))
    ) {
      user?.isLoggedIn && createEventV2ForFlyerImage()
    }
  }, [previewUrl, isCreatingEmptyEvent])

  const handleDateTimeConfirm = ({
    startDate,
    startTime,
    endDate,
    endTime,
  }: {
    startDate: string
    startTime: string
    endDate: string
    endTime: string
  }) => {
    setValue("startDate", startDate, {
      shouldValidate: true,
      shouldDirty: true,
    })
    setValue("startTime", startTime, {
      shouldValidate: true,
      shouldDirty: true,
    })
    setValue("endDate", endDate, { shouldValidate: true, shouldDirty: true })
    setValue("endTime", endTime, { shouldValidate: true, shouldDirty: true })
  }

  const locationBottomSheetRef = useRef<LocationBottomSheetHandle>(null)
  const onOpenLocationBottomSheet = () => {
    locationBottomSheetRef.current?.openModal()
  }

  const onSubmit = async (data: EventFormData) => {
    setIsLoading(true)

    let payloadData: any = {
      eventName: data?.eventName,
      eventDescription: data?.description,
      eventStatus: EVENT_STATUS.OPEN,
      eventClass:
        tickets.length > 0
          ? EVENT_CLASS.TICKETED_EVENT
          : EVENT_CLASS.NON_TICKETED_EVENT,
      tickets: tickets.length > 0 ? tickets : undefined, // Include tickets in payload
    }
    if (createdEventId) {
      payloadData.eventId = createdEventId
    }

    if (!isUploaded.flyerImage) {
      const finalFlyerImage = await checkImageType(formState?.flyerImage)
      payloadData.flyerImage = finalFlyerImage
    }

    if (!isUploaded.timingAndLocation) {
      payloadData.timing = {
        eventDate: convertToUTC(
          `${formState?.startDate} ${formState?.startTime}`,
        ),
        eventEndDate: convertToUTC(
          `${formState?.endDate} ${formState?.endTime}`,
        ),
        timeZone: "UTC",
        eventStartTime: convertToUTC(
          `${formState?.startDate} ${formState?.startTime}`,
        ),
        eventEndTime: convertToUTC(
          `${formState?.endDate} ${formState?.endTime}`,
        ),
        signupCloseDate: convertToUTC(
          `${formState?.startDate} ${formState?.startTime}`,
        ),
        signupCloseTime: convertToUTC(
          `${formState?.startDate} ${formState?.startTime}`,
        ),
        displayTimezone: timezone,
      }
      payloadData.location = {
        eventLocation: formState?.location?.nameOfPlace,
        latitude: formState?.location?.lat,
        longitude: formState?.location?.lon,
        guestLocationPreview: await getTheLocationInfo({
          latitude: formState?.location?.lat,
          longitude: formState?.location?.lon,
        }),
      }
      payloadData = {
        ...payloadData,
        whoCanJoin: EEVENT_INVITATION_CRITERIA.INVITE_BY_LINK_ONLY,
        eventType: EEventType.FREE_INDIVIDUAL,
        restrictMemberGender: defaultPayloadData.restrictMemberGender,
        numberAttending: defaultPayloadData.numberAttending,
        numberRemainingSpots: defaultPayloadData.numberRemainingSpots,
        minAge: defaultPayloadData.minAge,
        maxAge: defaultPayloadData.maxAge,
        invitationCriteria: EEVENT_INVITATION_CRITERIA.INVITE_BY_LINK_ONLY,
        howManyPeople: defaultPayloadData.howManyPeople,
      }
      if (templateState?.publicEvent) {
        payloadData.publicEvent = templateState.publicEvent
      }
      if (templateState?.category) {
        payloadData.category = templateState.category
      }
    }
    await createEventWeb({ ...payloadData })
      .then(async (res: any) => {
        if (res?.success) {
          refresh()
          navigate(
            `${ROUTES.EVENT_CREATE_SUCCESS}?eventId=${res.data.eventId}`,
            {
              state: {
                eventName: data?.eventName,
                flyerImage: flyerImageUrl,
                eventId: res.data.eventId,
              },
              replace: true, // Replace the current history entry
            },
          )
          setIsLoading(false)
        } else {
          showErrorToast(
            res?.message || "Something went wrong, please try again later",
          )
          setIsLoading(false)
        }
      })
      .catch((err) => {
        showErrorToast(
          err?.message || "Something went wrong while saving the draft",
        )
        setIsLoading(false)
      })
  }

  const eventPluginBottomSheetRef = useRef<EventPluginBottomSheetHandle>(null)

  const handleDescriptionChange = (value: string) => {
    setValue("description", value, {
      shouldValidate: true,
      shouldDirty: true,
    })
  }

  const handleLocationChange = (value: any) => {
    setValue("location", value.location, {
      shouldValidate: true,
      shouldDirty: true,
    })
  }

  useEffect(() => {
    if (
      fastSignUpStage == "done" &&
      user?.isLoggedIn &&
      isGoliveClicked.current
    ) {
      trigger().then((res) => {
        if (res) {
          handleSubmitWithValidation()
        }
      })
    }
  }, [fastSignUpStage])

  const handleSubmitWithValidation = () => {
    // This will run validation before submitting
    isGoliveClicked.current = true

    handleSubmit(
      (data) => {
        if (!user?.isLoggedIn) {
          setShowFastSignUpPopUp(true)
          return // Exit early to prevent form submission
        }

        if (tickets.length > 0 && !user?.emailVerified) {
          setShowEmailVerificationPopup(true)
          return
        }
        onSubmit(data)
      },
      (errors) => {
        // This callback runs when there are validation errors
        setIsLoading(false)
        console.log("Form errors:", errors)

        // Generic error message
        showErrorToast("Missing required fields")
        isGoliveClicked.current = false
      },
    )()
  }

  const createEventDirectly = async () => {
    handleSubmit(
      (data) => {
        onSubmit(data)
      },
      (errors) => {
        // This callback runs when there are validation errors
        setIsLoading(false)
        console.log("Form errors:", errors)

        // Generic error message
        showErrorToast("Missing required fields")
        isGoliveClicked.current = false
      },
    )()
  }

  const handleTicketsClick = () => {
    setShowMore(false)

    // No need to create empty event - tickets can be managed locally
    // Event creation will happen when user goes live with all data including tickets

    // Allow anyone to create tickets immediately - no form validation required
    setShowTicketModal(true)
  }

  const createEventV2ForData = async () => {
    try {
      let payloadData: any = {}

      if (createdEventId) {
        payloadData.eventId = createdEventId
      }

      if (!isUploaded.timingAndLocation) {
        payloadData = {
          ...payloadData,
          eventClass:
            tickets.length > 0
              ? EVENT_CLASS.TICKETED_EVENT
              : EVENT_CLASS.NON_TICKETED_EVENT,
          whoCanJoin: EEVENT_INVITATION_CRITERIA.INVITE_BY_LINK_ONLY,
          eventType: EEventType.FREE_INDIVIDUAL,
          eventStatus: EVENT_STATUS.DRAFT,
          restrictMemberGender: defaultPayloadData.restrictMemberGender,
          numberAttending: defaultPayloadData.numberAttending,
          numberRemainingSpots: defaultPayloadData.numberRemainingSpots,
          minAge: defaultPayloadData.minAge,
          maxAge: defaultPayloadData.maxAge,
          invitationCriteria: EEVENT_INVITATION_CRITERIA.INVITE_BY_LINK_ONLY,
          howManyPeople: defaultPayloadData.howManyPeople,
        }
        if (templateState?.publicEvent) {
          payloadData.publicEvent = templateState.publicEvent
        }
        if (templateState?.category) {
          payloadData.category = templateState.category
        }
      }

      payloadData = {
        ...payloadData,
        eventClass:
          tickets.length > 0
            ? EVENT_CLASS.TICKETED_EVENT
            : EVENT_CLASS.NON_TICKETED_EVENT,
        timing: {
          eventDate: convertToUTC(
            `${formState?.startDate} ${formState?.startTime}`,
          ),
          eventEndDate: convertToUTC(
            `${formState?.endDate} ${formState?.endTime}`,
          ),
          timeZone: "UTC",
          eventStartTime: convertToUTC(
            `${formState?.startDate} ${formState?.startTime}`,
          ),
          eventEndTime: convertToUTC(
            `${formState?.endDate} ${formState?.endTime}`,
          ),
          signupCloseDate: convertToUTC(
            `${formState?.startDate} ${formState?.startTime}`,
          ),
          signupCloseTime: convertToUTC(
            `${formState?.startDate} ${formState?.startTime}`,
          ),
          displayTimezone: timezone,
        },
        location: {
          eventLocation: formState?.location?.nameOfPlace,
          latitude: formState?.location?.lat,
          longitude: formState?.location?.lon,
          guestLocationPreview: await getTheLocationInfo({
            latitude: formState?.location?.lat,
            longitude: formState?.location?.lon,
          }),
        },
      }

      await createEventWeb({ ...payloadData }).then((res) => {
        if (res.success) {
          setIsUploaded({ ...isUploaded, timingAndLocation: true })
          if (!createdEventId) {
            setCreatedEventId(res.data.eventId)
          }
          if (!isUploaded.flyerImage) {
            createEventV2ForFlyerImage(res.data.eventId)
          }
        } else if (!res.success) {
          setIsUploaded({ ...isUploaded, timingAndLocation: false })
        }
      })
    } catch (error: any) {
      // Handle auth errors gracefully - likely stale token
      console.error("Auto-draft creation failed:", error)
      if (error.message?.includes("Authentication required")) {
        console.log("Auth token appears stale, user should sign in again")
        // Silently fail for auto-drafts, don't show error to user
        // The user will get proper auth flow when they click "Go live"
      }
    }
  }
  const hasMounted = useRef(false)

  useEffect(() => {
    if (hasMounted.current) {
      if (
        !isDatePickerModalOpen &&
        (dirtyFields.endDate ||
          dirtyFields.startDate ||
          dirtyFields.endTime ||
          dirtyFields.startTime ||
          dirtyFields.location)
      ) {
        // Only auto-create if user is logged in AND has valid auth token
        if (user?.isLoggedIn && auth.currentUser?.uid) {
          createEventV2ForData()
        }
      }
    } else {
      hasMounted.current = true
    }
  }, [location, isDatePickerModalOpen])

  const createEmptyEvent = async () => {
    try {
      await createEventWeb({
        eventStatus: EVENT_STATUS.DRAFT,
      }).then((res) => {
        if (res.success) {
          setCreatedEventId(res.data.eventId)
          setIsCreatingEmptyEvent(false)
        }
      })
    } catch (error: any) {
      // Handle auth errors gracefully - likely stale token
      console.error("Empty event creation failed:", error)
      setIsCreatingEmptyEvent(false)
      if (error.message?.includes("Authentication required")) {
        console.log("Auth token appears stale, user should sign in again")
        // Silently fail for auto-creation, don't show error to user
      }
    }
  }

  const { showAlert } = useAlert()

  const onCancelPress = () => {
    showAlert(
      "Are you sure you want to cancel this event?",
      "If you cancel now, all the progress you've made on this event will be lost.",
      "Yes, Cancel",
      () => {
        handleCancelPress()
      },
      "Keep Editing",
      () => {},
    )
  }

  const handleCancelPress = () => {
    const canGoBack = locationKey !== "default"

    if (!isEmpty(state?.template)) {
      navigate(`${ROUTES.EVENT_TEMPLATES}?category=${state.category}`)
      return
    }

    if (canGoBack) {
      navigate(-1)
      return
    }

    if (user?.isLoggedIn || isEmpty(referenceFrom)) {
      navigate(ROUTES.LANDING)
    } else {
      window.location.href = ensureLetsHangHttps(referenceFrom)
    }
  }

  const flyerInputRef = useRef<HTMLInputElement>(null)

  if (showMore) {
    return (
      <MoreComponent
        flyerImageUrl={flyerImageUrl}
        onBack={() => setShowMore(false)}
        eventId={createdEventId}
        onTicketsClick={handleTicketsClick}
      />
    )
  }

  return (
    <BackgroundWrapper
      bgImage={flyerImageUrl}
      className={`min-h-[100vh] h-full relative touch-manipulation`}
    >
      {isLoading && <LoadingSpinner />}
      <BlurWrapper
        blurKey={get(blurKeys, "mainBlurView")}
        overlayOpacityDefault="75"
        overlayColorDefault="dark"
        className=" bg-gray-400/10 h-[10vh] flex flex-row  items-center px-6  top-0 left-0 w-full z-10 fixed"
      >
        <div
          className="text-white/30 text-base cursor-pointer"
          onClick={onCancelPress}
        >
          Cancel
        </div>
        <div className="text-white text-base cursor-pointer flex-1">
          New event
        </div>
        <div
          className="text-[#30D158] text-base cursor-pointer"
          onClick={handleSubmitWithValidation}
        >
          Go live
        </div>
      </BlurWrapper>

      <BlurWrapper
        blurKey={get(blurKeys, "mainBlurView")}
        overlayOpacityDefault="75"
        overlayColorDefault="dark"
        className="min-h-[100vh] h-full flex flex-col overflow-auto relative"
      >
        <div className="flex flex-col justify-center items-center p-6 w-full max-w-2xl mx-auto mt-[10vh]">
          <form
            className="w-full space-y-6 flex flex-col justify-center items-center"
            onSubmit={(e) => {
              e.preventDefault()
              handleSubmitWithValidation()
            }}
          >
            <div className="space-y-2 relative ">
              <img
                src={flyerImageUrl}
                alt="Flyer"
                className="h-52 w-56 rounded-lg object-cover cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation()
                  flyerInputRef.current?.click()
                }}
              />

              <BlurWrapper
                overlayOpacityDefault="30"
                overlayColorDefault="dark"
                className="h-10 w-10 rounded-full flex justify-center items-center absolute bottom-2 right-2"
                onClick={() => {
                  setReDirectedFrom(null)
                  flyerInputRef.current?.click()
                }}
              >
                <div>
                  <FiEdit className="text-white text-lg" />
                </div>
                <input
                  id="flyerImageInput"
                  type="file"
                  accept="image/jpeg,image/png"
                  {...register("flyerImage")}
                  ref={flyerInputRef}
                  onClick={() => {
                    if (!createdEventId) {
                      setIsCreatingEmptyEvent(true)
                      // Only auto-create if user is logged in AND has valid auth token
                      if (user?.isLoggedIn && auth.currentUser?.uid) {
                        createEmptyEvent()
                      }
                    }
                  }}
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null
                    if (file) {
                      const fileType = file.type.toLowerCase()
                      if (
                        fileType !== "image/jpeg" &&
                        fileType !== "image/png"
                      ) {
                        alert("Please select only JPG or PNG images")
                        e.target.value = ""
                        return
                      }
                    }
                    setFlyerFile(file)
                    setValue("flyerImage", e.target.files[0] as any)
                  }}
                  className="hidden"
                />
              </BlurWrapper>
            </div>
            <div
              className={`space-y-2 w-full rounded-2xl bg-white/10  p-4 ${errors.eventName ? "border-2 border-red-400" : ""}`}
              style={{
                border: errors.eventName
                  ? undefined
                  : "1px solid rgba(255, 255, 255, 0.50)",
              }}
            >
              <input
                {...register("eventName")}
                className="w-full rounded-lg bg-transparent text-white focus:outline-none text-2xl"
                placeholder="Name your event"
              />
            </div>

            <div
              className={`space-y-2 w-full rounded-2xl p-4 bg-white/10 ${
                // Only highlight container if multiple fields are missing
                errors.startDate ||
                errors.endDate ||
                errors.startTime ||
                errors.endTime ||
                errors.location
                  ? "border-2 border-red-400"
                  : ""
              }`}
              style={{
                border:
                  errors.startDate ||
                  errors.endDate ||
                  errors.startTime ||
                  errors.endTime ||
                  errors.location
                    ? undefined
                    : "1px solid rgba(255, 255, 255, 0.50)",
              }}
            >
              <div
                className={`flex flex-row gap-4 items-center py-2 rounded-lg `}
                onClick={onOpenDateTimePickerModal}
              >
                <span className="text-2xl" role="img" aria-label="calendar">
                  🗓️
                </span>
                <input
                  type="text"
                  className="w-full rounded-lg bg-transparent text-white focus:outline-none text-base"
                  placeholder="Date and time"
                  value={`${startDate}, ${startTime}`}
                />
              </div>
              <hr className="w-full border-white/30" />
              {/* Location Row */}
              <div
                className={`flex items-center py-3 cursor-pointer rounded-lg`}
                onClick={onOpenLocationBottomSheet}
              >
                <span
                  className="text-2xl min-w-[32px] flex items-center"
                  role="img"
                  aria-label="location"
                >
                  📍
                </span>
                <div className="ml-2 flex flex-1 items-center w-full">
                  <div className="flex-1 w-full">
                    <input
                      className="bg-transparent w-full focus:outline-none text-white text-base truncate"
                      placeholder="Location"
                      value={location?.nameOfPlace || ""}
                      readOnly
                    />
                  </div>
                </div>
              </div>
              {/* Ticket Row */}
              {/* {ticketedEventsWebFeatureFlag && ( */}
              <>
                <hr className="w-full border-white/10 my-0" />
                <div
                  className="flex flex-row items-start py-3 cursor-pointer"
                  onClick={handleTicketsClick}
                >
                  <span
                    className="text-2xl mr-3 flex-shrink-0 flex items-center h-full"
                    role="img"
                    aria-label="ticket"
                  >
                    💵
                  </span>
                  <div className="flex flex-col items-start">
                    {/* {hasTickets ? (
                        <>
                          <span className="text-base text-white font-normal">
                            {isSingleTicket
                              ? `$${minTicketPrice}`
                              : `$${minTicketPrice} - $${maxTicketPrice}`}
                          </span>
                          <span className="text-xs text-white/50 mt-1 text-left">
                            {tickets.length} ticket
                            {tickets.length !== 1 ? "s" : ""} created
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="text-base text-white/50 font-normal">
                            Add tickets (optional)
                          </span>
                          <span className="text-xs italic text-white/25 mt-1 text-left">
                            Tickets can only be created before going live
                          </span>
                        </>
                      )} */}

                    <>
                      <span className="text-base text-white/50 font-normal">
                        Add tickets (optional)
                      </span>
                      <span className="text-xs italic text-white/25 mt-1 text-left">
                        Tickets can only be created before going live
                      </span>
                    </>
                  </div>
                </div>
              </>
              {/* )} */}
            </div>
            <VideoPreviewExperiment />
            <div
              className={`space-y-2 w-full rounded-2xl p-4 bg-white/10 ${errors.description ? "border-2 border-red-400" : ""}`}
              style={{
                border: errors.description
                  ? undefined
                  : "1px solid rgba(255, 255, 255, 0.50)",
              }}
              onClick={onOpenDescriptionBottomSheet}
            >
              <textarea
                // {...register("description")}
                className="w-full  bg-transparent text-white min-h-[150px] text-base focus:outline-none resize-none"
                placeholder="Describe your event"
                value={description}
              />
            </div>

            {/* Plugins Section */}
            {isIntegrationsEnabled && (
              <div className="space-y-4 w-full">
                {/* Show existing plugins if event exists */}
                {createdEventId && eventState?.eventDetails?.eventDocData && (
                  <EventPluginsRenderer
                    eventDocData={eventState.eventDetails.eventDocData}
                    eventId={createdEventId}
                    user={user}
                    isAdmin={true}
                    unCachedLocation={{}}
                  />
                )}

                {/* Always show add plugins section */}
                <div
                  className="mt-0 sm:mt-0 p-4 rounded-2xl w-full cursor-pointer bg-white/10"
                  style={{
                    border: "1px solid rgba(255, 255, 255, 0.50)",
                  }}
                >
                  <EventAddAPluginSection
                    eventId={createdEventId}
                    bottomSheetRef={eventPluginBottomSheetRef}
                    onEventCreated={(eventId) => {
                      setCreatedEventId(eventId)
                    }}
                    user={user}
                    onShowFastSignUp={() => setShowFastSignUpPopUp(true)}
                    backgroundStyle={{
                      containerClassName:
                        "mt-0 sm:mt-0 p-0 rounded-none w-full cursor-pointer bg-transparent border-none",
                    }}
                  />
                </div>
              </div>
            )}
          </form>
        </div>
      </BlurWrapper>
      <DateTimePickerModal
        ref={dateTimePickerModalRef}
        startDate={startDate}
        startTime={startTime}
        endDate={endDate}
        endTime={endTime}
        onConfirm={handleDateTimeConfirm}
        setIsDatePickerModalOpen={setIsDatePickerModalOpen}
      />

      <DescriptionBottomSheet
        ref={descriptionBottomSheetRef}
        register={register}
        errors={errors}
        description={description}
        onChange={handleDescriptionChange}
      />

      <LocationBottomSheet
        ref={locationBottomSheetRef}
        value={{ location: location }}
        onLocationSelected={handleLocationChange}
      />

      <FastSignUpComponentWithGTM
        isOpen={showFastSignUpPopUp}
        setOpenState={setShowFastSignUpPopUp}
        title="Let's get you started"
        showTitleAsIs={true}
        verifyEmail={tickets.length > 0 && !auth.currentUser?.emailVerified}
      />

      <TicketModal
        isOpen={showTicketModal}
        onClose={() => setShowTicketModal(false)}
        eventId={createdEventId}
        localTickets={tickets}
        onLocalTicketUpdate={setTickets}
      />
      <EventPluginBottomSheet
        ref={eventPluginBottomSheetRef}
        eventId={createdEventId}
        onEventCreated={(eventId) => {
          setCreatedEventId(eventId)
        }}
      />
      {showEmailVerificationPopup && (
        <EmailVerificationPopup
          isLoading={loadingEmailVerification}
          isEmailInboxVerification={isEmailInboxVerification}
          onClose={handleEmailVerificationClose}
          onVerifyEmail={onVerifyEmail}
        />
      )}
    </BackgroundWrapper>
  )
}

export default CreateEventPage
